import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import {
  ApplicationUsersResponse,
  CompanyProductsResponse,
  CrunchTimeApplicationUser,
  CrunchTimeCompanyProduct,
  CrunchTimeLocation,
  CrunchTimePackageType,
  CrunchTimeRecipe,
  FetchCompanyProductsParams,
  FetchLocationsParams,
  FetchRecipesParams,
  FetchWasteAdjustmentsParams,
  GetWasteProductsResponse,
  LocationsResponse,
  PackageTypesResponse,
  PublishWastePayload,
  RecipesResponse,
  SaveWasteDayPayload,
  WasteAdjustmentsResponse,
  CrunchTimeInventoryAdjustment,
  CreateWasteEntryPayload,
  WasteLogEntryResponse,
  SaveWasteLogPayload,
  UpdateWasteLogPayload,
  roundQuantityForCrunchTime,
  WasteProductItem,
  resolveWasteInventoryHeaderFlagLetter,
  resolveInventoryStorageCode,
  isWasteProductStorageCodeOptional,
  WASTE_REASON_CODE_TO_LABEL,
  normalizeWasteMemoForApi
} from '@/src/types/apps/gygWasteLogging'

interface EonWasteLoggingState {
  adjustments: WasteAdjustmentsResponse['data']
  adjustmentsLoading: boolean

  // Locations list (for reference)
  locations: CrunchTimeLocation[]
  locationsLoading: boolean

  // Single location details (for period / lastInventoryPost)
  locationDetails: CrunchTimeLocation | null
  locationDetailsLoading: boolean

  // Recipes
  recipes: CrunchTimeRecipe[]
  recipesLoading: boolean

  // Package types
  packageTypes: CrunchTimePackageType[]
  packageTypesLoading: boolean

  // Application users
  applicationUsers: CrunchTimeApplicationUser[]
  applicationUsersLoading: boolean

  // Company products
  companyProducts: CrunchTimeCompanyProduct[]
  companyProductsLoading: boolean

  // Waste products from GET /crunchtime/waste-products (concept + UDC); used when present for tabs
  wasteProducts: WasteProductItem[]
  wasteProductsLoading: boolean

  /**
   * Concept for which waste-products fetch has completed (fulfilled or rejected). Stops auto-refetch loops when
   * the request fails (e.g. CORS) while wasteProducts stays empty and wasteProductsLoading becomes false.
   */
  wasteProductsHydratedForConcept: string | null

  // Per-day save tracking
  savedDays: string[]
  saveLoading: boolean

  // Waste log from our backend (GET /eon/waste-log) for pre-fill
  wasteLogEntries: WasteLogEntryResponse[]
  wasteLogDate: string | null
  wasteLogLoading: boolean

  submitLoading: boolean
  isPublished: boolean
  publishReady: boolean
  success: string | null
  error: string | null

  /** Primary storage codes from GET crunchtime/location-product-pricing (productNumber -> storageCode). Fills missing storageCode for publish. */
  primaryStorageCodes: Record<string, string>
}

const initialState: EonWasteLoggingState = {
  adjustments: [],
  adjustmentsLoading: false,
  locations: [],
  locationsLoading: false,
  locationDetails: null,
  locationDetailsLoading: false,
  recipes: [],
  recipesLoading: false,
  packageTypes: [],
  packageTypesLoading: false,
  applicationUsers: [],
  applicationUsersLoading: false,
  companyProducts: [],
  companyProductsLoading: false,
  wasteProducts: [],
  wasteProductsLoading: false,
  wasteProductsHydratedForConcept: null,
  savedDays: [],
  saveLoading: false,
  wasteLogEntries: [],
  wasteLogDate: null,
  wasteLogLoading: false,
  submitLoading: false,
  isPublished: false,
  publishReady: false,
  success: null,
  error: null,
  primaryStorageCodes: {}
}

// ── Inventory ─────────────────────────────────────────────────────────────

/** Transaction / PO key for matching list rows to includeDetails responses (quantity or value / recipe blocks). */
const crunchTimeAdjustmentTransactionKey = (adj: CrunchTimeInventoryAdjustment): string | undefined => {
  const qh = adj.inventoryAdjustmentHeaderDetails
  if (qh?.transactionNumber != null && String(qh.transactionNumber).trim() !== '') {
    return String(qh.transactionNumber).trim()
  }
  const vh = adj.inventoryValueAdjustmentHeaderDetails as Record<string, unknown> | undefined
  if (vh) {
    const t = vh.transactionNumber ?? vh.TransactionNumber ?? vh.poNumber ?? vh.PoNumber
    if (t != null && String(t).trim() !== '') return String(t).trim()
  }

  return undefined
}

const crunchTimeDetailRecordMatchesTransaction = (d: CrunchTimeInventoryAdjustment, txNum: string): boolean => {
  const q = d.inventoryAdjustmentHeaderDetails?.transactionNumber
  if (q != null && String(q) === String(txNum)) return true
  const vh = d.inventoryValueAdjustmentHeaderDetails as Record<string, unknown> | undefined
  if (vh) {
    const t = vh.transactionNumber ?? vh.TransactionNumber ?? vh.poNumber ?? vh.PoNumber
    if (t != null && String(t) === String(txNum)) return true
  }

  return false
}

/** GET /crunchtime/inventory/adjustments
 *  Step 1: Fetch all adjustment headers for the given date.
 *  Step 2: For each row that has a transaction id, fetch its detail lines
 *          (includeDetails=true) and merge them back so the caller receives
 *          fully-enriched objects (quantity adjustments and value / recipe-style blocks).
 */
const EMPTY_ADJUSTMENTS: WasteAdjustmentsResponse = { success: true, count: 0, data: [] }

export const fetchWasteAdjustments = createAsyncThunk<WasteAdjustmentsResponse, FetchWasteAdjustmentsParams, Redux>(
  'gygEonWasteLogging/fetchWasteAdjustments',
  async ({ locationCode, entryDate }, { extra }) => {
    // Step 1 — headers
    let headersResp
    try {
      headersResp = await extra.httpClient.get('crunchtime/inventory/adjustments', {
        params: { locationCode, entryDate }
      })
    } catch (err: any) {
      const status = err?.response?.status

      // 404: no adjustments for date/location. 403: some envs/WAFs deny read; product asks to show same “no waste” UX.
      if (status === 404 || status === 403) {
        return EMPTY_ADJUSTMENTS
      }
      throw err
    }

    // ResponseTransformInterceptor wraps every response: { data: <actual body> }
    // Actual body shape: { success, count, data: [...] }
    const result: WasteAdjustmentsResponse = headersResp.data?.data ?? headersResp.data

    if (!Array.isArray(result.data) || result.data.length === 0) {
      return result
    }

    // Step 2 — enrich each adjustment with its detail lines
    const enriched = await Promise.all(
      result.data.map(async adj => {
        const txNum = crunchTimeAdjustmentTransactionKey(adj)
        if (!txNum) return adj

        try {
          const detailResp = await extra.httpClient.get('crunchtime/inventory/adjustments', {
            params: { locationCode, entryDate, transactionNumber: txNum, includeDetails: true }
          })
          const detailResult: WasteAdjustmentsResponse = detailResp.data?.data ?? detailResp.data

          const match = Array.isArray(detailResult.data)
            ? detailResult.data.find(d => crunchTimeDetailRecordMatchesTransaction(d, txNum))
            : undefined

          return match ?? adj
        } catch {
          return adj
        }
      })
    )

    return { ...result, data: enriched }
  }
)

const CRUNCHTIME_INVENTORY_RECIPE_BULK = 'crunchtime/inventory/inventory-recipe'
const CRUNCHTIME_INVENTORY_ADJUSTMENT_BULK = 'crunchtime/inventory/inventory-adjustment'

const PUBLISH_MAX_RETRIES = 3
const PUBLISH_INITIAL_BACKOFF_MS = 1000

async function postWithRetry<T>(postFn: () => Promise<T>, retries = PUBLISH_MAX_RETRIES): Promise<T> {
  let lastError: any
  let delayMs = PUBLISH_INITIAL_BACKOFF_MS
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await postFn()
    } catch (err: any) {
      lastError = err
      const status = err?.response?.status
      const isRetryable = status === 429 || (status >= 500 && status < 600) || !err?.response
      if (attempt === retries || !isRetryable) throw err
      await new Promise(r => setTimeout(r, delayMs))
      delayMs *= 2
    }
  }
  throw lastError
}

/**
 * Each payload line goes to exactly one bucket (mutually exclusive): Y = recipe bulk, N = inventory adjustment.
 * Same flag rules as the BFF (`resolveComponentRecipeFlag`). Two HTTP calls only when both buckets are non-empty.
 */
function splitWasteEntryPayloadsByRecipeFlag(payloads: CreateWasteEntryPayload[]): {
  recipeY: CreateWasteEntryPayload[]
  inventoryN: CreateWasteEntryPayload[]
} {
  const recipeY: CreateWasteEntryPayload[] = []
  const inventoryN: CreateWasteEntryPayload[] = []
  for (const p of payloads) {
    if (resolveWasteInventoryHeaderFlagLetter(p.productId, p.userDefinedCategoryCode) === 'Y') {
      recipeY.push(p)
    } else {
      inventoryN.push(p)
    }
  }

  return { recipeY, inventoryN }
}

/** POST inventory-recipe (Y) then inventory-adjustment (N) when both sides have lines. */
async function postCrunchtimeWasteInventoryBulks(
  httpClient: Redux['extra']['httpClient'],
  locationId: string,
  payloads: CreateWasteEntryPayload[],
  useRetry: boolean
): Promise<void> {
  const { recipeY, inventoryN } = splitWasteEntryPayloadsByRecipeFlag(payloads)
  const run = useRetry ? (fn: () => Promise<unknown>) => postWithRetry(fn) : (fn: () => Promise<unknown>) => fn()

  const errors: string[] = []
  if (recipeY.length) {
    try {
      await run(() =>
        httpClient.post(CRUNCHTIME_INVENTORY_RECIPE_BULK, {
          locationId,
          entries: recipeY
        })
      )
    } catch (err: any) {
      errors.push(err?.response?.data?.message || err?.message || 'Recipe (Y) bulk save failed')
    }
  }
  if (inventoryN.length) {
    try {
      await run(() =>
        httpClient.post(CRUNCHTIME_INVENTORY_ADJUSTMENT_BULK, {
          locationId,
          entries: inventoryN
        })
      )
    } catch (err: any) {
      errors.push(err?.response?.data?.message || err?.message || 'Inventory (N) bulk save failed')
    }
  }
  if (errors.length) {
    throw new Error(errors.join(' '))
  }
}

/**
 * POST …/inventory-recipe (Y) and/or …/inventory-adjustment (N) — up to two requests.
 * Does not lock the form (isPublished stays false).
 */
export const saveWasteDayEntries = createAsyncThunk<string, SaveWasteDayPayload, Redux>(
  'gygEonWasteLogging/saveWasteDayEntries',
  async ({ locationId, date, entries }, { extra, rejectWithValue }) => {
    try {
      const transactionDate = new Date(date).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })

      const payloads: CreateWasteEntryPayload[] = entries.map(entry => {
        const memoNorm = normalizeWasteMemoForApi(entry.memo)
        const memo = memoNorm ?? ''
        const udc =
          entry.userDefinedCategoryCode != null && String(entry.userDefinedCategoryCode).trim() !== ''
            ? String(entry.userDefinedCategoryCode).trim()
            : undefined
        const header: NonNullable<CreateWasteEntryPayload['header']> = {
          componentRecipeAdjustmentFlag: resolveWasteInventoryHeaderFlagLetter(entry.crunchTimeProductId, udc)
        }
        if (memoNorm != null) header.memo = memoNorm

        const storageTrimmed = resolveInventoryStorageCode(entry.storageCode)

        return {
          locationId,
          productId: entry.crunchTimeProductId,
          quantity: roundQuantityForCrunchTime(entry.quantity),
          qualitativeJudgment: memo ?? '',
          transactionDate,
          ...(entry.packageType ? { packageType: entry.packageType } : {}),
          ...(isWasteProductStorageCodeOptional(entry.crunchTimeProductId) || storageTrimmed === ''
            ? {}
            : { storageCode: storageTrimmed }),
          ...(udc != null ? { userDefinedCategoryCode: udc } : {}),
          header
        }
      })

      if (payloads.length === 0) {
        return rejectWithValue({ message: 'No waste entries to save to CrunchTime.' })
      }

      if (process.env.NODE_ENV !== 'production') {
        const { recipeY, inventoryN } = splitWasteEntryPayloadsByRecipeFlag(payloads)
        // eslint-disable-next-line no-console -- debug: CrunchTime inventory bulk payloads
        console.log(
          '[EonWaste] POST inventory-recipe / inventory-adjustment (saveWasteDayEntries)',
          JSON.stringify(
            {
              locationId,
              inventoryRecipeY: recipeY,
              inventoryAdjustmentN: inventoryN
            },
            null,
            2
          )
        )
      }

      await postCrunchtimeWasteInventoryBulks(extra.httpClient, locationId, payloads, false)

      return date
    } catch (error: any) {
      if (error.response) {
        const { message } = error.response.data ?? {}

        return rejectWithValue({ message: message || 'Failed to save waste entries to CrunchTime' })
      }

      return rejectWithValue({ message: error.message || 'Network error occurred' })
    }
  }
)

export const submitWasteEntries = createAsyncThunk<void, PublishWastePayload, Redux>(
  'gygEonWasteLogging/submitWasteEntries',
  async ({ locationId, daysToPublish, daypartCode, publishedEntryIds }, { extra, rejectWithValue }) => {
    try {
      for (const { entries } of daysToPublish) {
        for (const entry of entries) {
          if (entry.reason == null || String(entry.reason).trim() === '') {
            return rejectWithValue({
              message: `Reason is required to publish waste for product ${entry.crunchTimeProductId}.`
            })
          }
        }
      }

      const allPayloads: CreateWasteEntryPayload[] = daysToPublish.flatMap(({ date, entries }) => {
        const transactionDate = new Date(date).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })

        return entries.map(entry => {
          const reasonCode = String(entry.reason).trim()
          const qualitativeJudgment = WASTE_REASON_CODE_TO_LABEL[reasonCode] ?? reasonCode

          const memoNorm = normalizeWasteMemoForApi(entry.memo)
          const udc =
            entry.userDefinedCategoryCode != null && String(entry.userDefinedCategoryCode).trim() !== ''
              ? String(entry.userDefinedCategoryCode).trim()
              : undefined
          const header: NonNullable<CreateWasteEntryPayload['header']> = {
            componentRecipeAdjustmentFlag: resolveWasteInventoryHeaderFlagLetter(entry.crunchTimeProductId, udc)
          }
          if (daypartCode != null) header.daypartCode = daypartCode
          if (memoNorm != null) header.memo = memoNorm

          const storageTrimmed = resolveInventoryStorageCode(entry.storageCode)

          return {
            locationId,
            productId: entry.crunchTimeProductId,
            quantity: roundQuantityForCrunchTime(entry.quantity),
            qualitativeJudgment,
            transactionDate,
            ...(entry.packageType ? { packageType: entry.packageType } : {}),
            ...(isWasteProductStorageCodeOptional(entry.crunchTimeProductId) || storageTrimmed === ''
              ? {}
              : { storageCode: storageTrimmed }),
            header,
            ...(udc != null ? { userDefinedCategoryCode: udc } : {})
          }
        })
      })

      if (allPayloads.length === 0) {
        return rejectWithValue({ message: 'No entries to publish.' })
      }

      let batchFailed: {
        message: string
        failed?: { productId: string; transactionDate: string; message: string }[]
      } | null = null
      try {
        await postCrunchtimeWasteInventoryBulks(extra.httpClient, locationId, allPayloads, true)
      } catch (err: any) {
        const message = err?.message || err?.response?.data?.message || 'Request failed'
        batchFailed = {
          message,
          failed: allPayloads.map(p => ({
            productId: p.productId,
            transactionDate: p.transactionDate,
            message
          }))
        }
      }

      if (batchFailed != null) {
        return rejectWithValue({
          message: `Publish failed (${allPayloads.length} entries). ${batchFailed.message}`,
          failed: batchFailed.failed
        })
      }

      // Mark successfully published EON waste log entries as Published so they are no longer editable.
      if (publishedEntryIds?.length) {
        await extra.httpClient.post('eon/waste-log/mark-published', {
          entryIds: publishedEntryIds
        })
      }
    } catch (error: any) {
      if (error.response) {
        const { message } = error.response.data ?? {}

        return rejectWithValue({
          message: message || 'Failed to publish waste entries to CrunchTime'
        })
      }

      return rejectWithValue({ message: error.message || 'Network error occurred' })
    }
  }
)

// ── EON Waste Log (backend DB + CrunchTime) ────────────────────────────────

/** GET /eon/waste-log — Load saved breakdown (AM Prep, Lunch, PM Prep, Dinner) for a date. */
export const fetchWasteLog = createAsyncThunk<
  { date: string; data: WasteLogEntryResponse[] },
  { locationCode: string; date: string },
  Redux
>('gygEonWasteLogging/fetchWasteLog', async ({ locationCode, date }, { extra }) => {
  const res = await extra.httpClient.get('eon/waste-log', {
    params: { locationCode, date }
  })
  const body = res.data?.data ?? res.data
  const list = Array.isArray(body) ? body : (body?.data ?? [])

  return { date, data: list }
})

/**
 * POST /eon/waste-log — Save breakdown to DB only (no CrunchTime). Use Publish to push to CrunchTime.
 */
export const saveWasteLog = createAsyncThunk<string, SaveWasteLogPayload, Redux>(
  'gygEonWasteLogging/saveWasteLog',
  async (payload, { extra, rejectWithValue }) => {
    try {
      await extra.httpClient.post('eon/waste-log', payload)

      return payload.date
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to save waste log'

      return rejectWithValue({ message })
    }
  }
)

/** PATCH /eon/waste-log — Update existing entries (Manager Edit). Updates quantity and reason by id; does not insert. */
export const updateWasteLog = createAsyncThunk<number, UpdateWasteLogPayload, Redux>(
  'gygEonWasteLogging/updateWasteLog',
  async (payload, { extra, rejectWithValue }) => {
    try {
      const res = await extra.httpClient.patch('eon/waste-log', payload)
      const count = res.data?.data?.count ?? res.data?.count ?? payload.updates.length

      return count
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to update waste log'

      return rejectWithValue({ message })
    }
  }
)

// ── Locations ─────────────────────────────────────────────────────────────

/** GET /crunchtime/locations */
export const fetchCrunchTimeLocations = createAsyncThunk<LocationsResponse, FetchLocationsParams | void, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeLocations',
  async (params, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/locations', { params: params ?? {} })

    // Unwrap ResponseTransformInterceptor envelope: { data: <actual body> }
    return response.data?.data ?? response.data
  }
)

/**
 * GET /crunchtime/locations?locationCode=X
 * Fetches a single location by its CrunchTime code and stores it in locationDetails.
 * Used to read lastInventoryPost for period cycle calculation.
 */
export const fetchCrunchTimeLocationByCode = createAsyncThunk<CrunchTimeLocation, string, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeLocationByCode',
  async (locationCode, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/locations', {
      params: { locationCode }
    })

    // Unwrap interceptor envelope, then extract first matching location from the array
    const body: LocationsResponse = response.data?.data ?? response.data
    const location = Array.isArray(body.data) ? body.data[0] : null

    if (!location) {
      throw new Error(`CrunchTime location not found for code: ${locationCode}`)
    }

    return location
  }
)

/** GET /crunchtime/locations/:id */
export const fetchCrunchTimeLocationById = createAsyncThunk<CrunchTimeLocation, string, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeLocationById',
  async (locationId, { extra }) => {
    const response = await extra.httpClient.get(`crunchtime/locations/${locationId}`)

    return response.data
  }
)

// ── Recipes ───────────────────────────────────────────────────────────────

/** GET /crunchtime/recipes-enhanced-v2 */
export const fetchCrunchTimeRecipes = createAsyncThunk<RecipesResponse, FetchRecipesParams | void, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeRecipes',
  async (params, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/recipes-enhanced-v2', { params: params ?? {} })

    return response.data?.data ?? response.data
  }
)

// ── Package Types ─────────────────────────────────────────────────────────

/** GET /crunchtime/package-types-v2 */
export const fetchCrunchTimePackageTypes = createAsyncThunk<PackageTypesResponse, void, Redux>(
  'gygEonWasteLogging/fetchCrunchTimePackageTypes',
  async (_params, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/package-types-v2')

    return response.data?.data ?? response.data
  }
)

// ── Application Users ─────────────────────────────────────────────────────

/** GET /crunchtime/application-users */
export const fetchCrunchTimeApplicationUsers = createAsyncThunk<ApplicationUsersResponse, void, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeApplicationUsers',
  async (_params, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/application-users')

    return response.data?.data ?? response.data
  }
)

export const fetchCrunchTimeCompanyProducts = createAsyncThunk<
  CompanyProductsResponse,
  FetchCompanyProductsParams | void,
  Redux
>('gygEonWasteLogging/fetchCrunchTimeCompanyProducts', async (params, { extra }) => {
  const response = await extra.httpClient.get('crunchtime/company-products-enhanced-v1', {
    params: params ?? {}
  })

  return response.data?.data ?? response.data
})

const WASTE_PRODUCTS_CACHE_KEY_PREFIX = 'eon_waste_products_'
const WASTE_PRODUCTS_CACHE_TTL_MS = 15 * 60 * 1000 // 15 minutes

const getWasteProductsFromCache = (concept: string): GetWasteProductsResponse | null => {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(`${WASTE_PRODUCTS_CACHE_KEY_PREFIX}${concept}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { data: GetWasteProductsResponse['data']; concept: string; fetchedAt: number }
    if (!Array.isArray(parsed.data) || typeof parsed.fetchedAt !== 'number') return null
    if (Date.now() - parsed.fetchedAt > WASTE_PRODUCTS_CACHE_TTL_MS) return null

    return { concept: parsed.concept ?? concept, data: parsed.data }
  } catch {
    return null
  }
}

const setWasteProductsCache = (concept: string, payload: GetWasteProductsResponse): void => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(
      `${WASTE_PRODUCTS_CACHE_KEY_PREFIX}${concept}`,
      JSON.stringify({ data: payload.data, concept: payload.concept, fetchedAt: Date.now() })
    )
  } catch {
    // ignore cache write errors
  }
}

const PRIMARY_STORAGE_CACHE_KEY_PREFIX = 'eon_location_product_pricing_'
const PRIMARY_STORAGE_CACHE_TTL_MS = 15 * 60 * 1000 // 15 minutes (align with waste-products cache)

type PrimaryStorageSessionCache = { map: Record<string, string>; fetchedAt: number }

const readPrimaryStorageSessionCache = (locationCode: string): PrimaryStorageSessionCache | null => {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(`${PRIMARY_STORAGE_CACHE_KEY_PREFIX}${locationCode}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PrimaryStorageSessionCache
    if (!parsed || typeof parsed.map !== 'object' || parsed.map === null || typeof parsed.fetchedAt !== 'number') {
      return null
    }
    if (Date.now() - parsed.fetchedAt > PRIMARY_STORAGE_CACHE_TTL_MS) return null

    return parsed
  } catch {
    return null
  }
}

const writePrimaryStorageSessionCache = (locationCode: string, map: Record<string, string>): void => {
  if (typeof sessionStorage === 'undefined') return
  try {
    const payload: PrimaryStorageSessionCache = { map, fetchedAt: Date.now() }
    sessionStorage.setItem(`${PRIMARY_STORAGE_CACHE_KEY_PREFIX}${locationCode}`, JSON.stringify(payload))
  } catch {
    // ignore cache write errors
  }
}

/** Build payload for Redux: only requested ids with non-empty storage codes. */
const pickPrimaryStoragePayload = (requestedIds: string[], map: Record<string, string>): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const id of requestedIds) {
    const v = map[id]
    if (v != null && String(v).trim() !== '') {
      out[id] = String(v).trim()
    }
  }

  return out
}

/** GET /crunchtime/location-product-pricing — Primary storage code per product at location. Used when adjustments/waste log have packageType but no storageCode. Cached in sessionStorage (15 min) per location with merge-by-product. */
export const fetchPrimaryStorageCodes = createAsyncThunk<
  Record<string, string>,
  { locationCode: string; productNumbers: string[] },
  Redux
>('gygEonWasteLogging/fetchPrimaryStorageCodes', async ({ locationCode, productNumbers }, { extra }) => {
  if (!locationCode?.trim() || productNumbers.length === 0) return {}

  const loc = locationCode.trim()
  const normalizedIds = [...new Set(productNumbers.map(p => String(p).trim()).filter(Boolean))]

  const cached = readPrimaryStorageSessionCache(loc)
  const mergedMap: Record<string, string> = cached ? { ...cached.map } : {}

  const missing = normalizedIds.filter(id => !Object.prototype.hasOwnProperty.call(mergedMap, id))

  if (missing.length === 0) {
    return pickPrimaryStoragePayload(normalizedIds, mergedMap)
  }

  const productNumbersParam = missing.slice(0, 100).join(',')
  const response = await extra.httpClient.get('crunchtime/location-product-pricing', {
    params: { locationCode: loc, productNumbers: productNumbersParam }
  })
  const raw = response.data?.data ?? response.data
  const chunk: Record<string, string> =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, string>) : {}

  const batch = missing.slice(0, 100)
  for (const [k, v] of Object.entries(chunk)) {
    const key = String(k).trim()
    mergedMap[key] = v != null && String(v).trim() !== '' ? String(v).trim() : ''
  }
  for (const id of batch) {
    if (!Object.prototype.hasOwnProperty.call(mergedMap, id)) mergedMap[id] = ''
  }

  writePrimaryStorageSessionCache(loc, mergedMap)

  return pickPrimaryStoragePayload(normalizedIds, mergedMap)
})

/** GET /crunchtime/waste-products?concept=X — Aggregated waste products (concept + UDC). Use for waste-logging tabs. Cached in sessionStorage (15 min) and by backend. */
export const fetchWasteProducts = createAsyncThunk<GetWasteProductsResponse, { concept: string }, Redux>(
  'gygEonWasteLogging/fetchWasteProducts',
  async ({ concept }, { extra }) => {
    const conceptVal = (concept ?? '').trim() || 'AUWASTE'
    const cached = getWasteProductsFromCache(conceptVal)
    if (cached) {
      return cached
    }

    try {
      const response = await extra.httpClient.get('crunchtime/waste-products', {
        params: { concept: conceptVal }
      })
      const body = response.data?.data ?? response.data
      const data = Array.isArray(body?.data) ? body.data : []
      const payload: GetWasteProductsResponse = {
        concept: body?.concept ?? conceptVal,
        data
      }
      setWasteProductsCache(conceptVal, payload)

      return payload
    } catch (err: unknown) {
      throw err
    }
  }
)

// ── Slice ─────────────────────────────────────────────────────────────────

const gygEonWasteLoggingSlice = createSlice({
  name: 'gygEonWasteLogging',
  initialState,
  reducers: {
    resetWasteLoggingState(state) {
      state.success = null
      state.error = null
      state.wasteProducts = []
      state.wasteProductsHydratedForConcept = null
    },
    setPublished(state, action: { payload: boolean }) {
      state.isPublished = action.payload
    },
    setPublishReady(state, action: { payload: boolean }) {
      state.publishReady = action.payload
    }
  },
  extraReducers: builder => {
    builder

      // Inventory adjustments
      .addCase(fetchWasteAdjustments.pending, state => {
        state.adjustmentsLoading = true
        state.error = null
      })
      .addCase(fetchWasteAdjustments.fulfilled, (state, action) => {
        state.adjustmentsLoading = false

        // payload is already unwrapped in the thunk → data is the CrunchTimeInventoryAdjustment[]
        const data = action.payload?.data
        state.adjustments = Array.isArray(data) ? data : []
      })
      .addCase(fetchWasteAdjustments.rejected, (state, action) => {
        state.adjustmentsLoading = false
        state.error = action.error.message ?? 'Failed to fetch waste adjustments'
      })

      // Save single day
      .addCase(saveWasteDayEntries.pending, state => {
        state.saveLoading = true
        state.error = null
      })
      .addCase(saveWasteDayEntries.fulfilled, (state, action) => {
        state.saveLoading = false

        // action.payload is the date string returned by the thunk
        if (!state.savedDays.includes(action.payload)) {
          state.savedDays.push(action.payload)
        }
        state.success = `Waste saved for ${action.payload}`
      })
      .addCase(saveWasteDayEntries.rejected, (state, action) => {
        state.saveLoading = false
        state.error = (action.payload as any)?.message ?? action.error.message ?? 'Failed to save waste entries'
      })

      // Publish period
      .addCase(submitWasteEntries.pending, state => {
        state.submitLoading = true
        state.success = null
        state.error = null
      })
      .addCase(submitWasteEntries.fulfilled, state => {
        state.submitLoading = false
        state.isPublished = true
        state.success = 'Waste log published successfully'
      })
      .addCase(submitWasteEntries.rejected, (state, action) => {
        state.submitLoading = false
        state.error = (action.payload as any)?.message ?? action.error.message ?? 'Failed to publish waste log'
      })

      // GET /eon/waste-log (backend breakdown for pre-fill)
      .addCase(fetchWasteLog.pending, state => {
        state.wasteLogLoading = true
      })
      .addCase(fetchWasteLog.fulfilled, (state, action) => {
        state.wasteLogLoading = false
        state.wasteLogDate = action.payload.date
        state.wasteLogEntries = Array.isArray(action.payload.data) ? action.payload.data : []

        // Treat dates with backend entries as saved so Publish doesn't re-save and UI shows correct status
        if (state.wasteLogEntries.length > 0 && action.payload.date && !state.savedDays.includes(action.payload.date)) {
          state.savedDays.push(action.payload.date)
        }
      })
      .addCase(fetchWasteLog.rejected, state => {
        state.wasteLogLoading = false
        state.wasteLogEntries = []
        state.wasteLogDate = null
      })

      .addCase(fetchPrimaryStorageCodes.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === 'object') {
          state.primaryStorageCodes = { ...state.primaryStorageCodes, ...action.payload }
        }
      })

      // POST /eon/waste-log (save to DB only; Publish uses submitWasteEntries → inventory-recipe / inventory-adjustment)
      .addCase(saveWasteLog.pending, state => {
        state.saveLoading = true
        state.error = null
      })
      .addCase(saveWasteLog.fulfilled, (state, action) => {
        state.saveLoading = false
        if (action.payload && !state.savedDays.includes(action.payload)) {
          state.savedDays.push(action.payload)
        }
        state.success = `Waste saved for ${action.payload}`
      })
      .addCase(saveWasteLog.rejected, (state, action) => {
        state.saveLoading = false
        state.error = (action.payload as any)?.message ?? action.error.message ?? 'Failed to save waste log'
      })

      // PATCH /eon/waste-log (Manager Edit: update existing records)
      .addCase(updateWasteLog.pending, state => {
        state.saveLoading = true
        state.error = null
      })
      .addCase(updateWasteLog.fulfilled, (state, action) => {
        state.saveLoading = false
        state.success = `Waste log updated (${action.payload} record${action.payload !== 1 ? 's' : ''})`
      })
      .addCase(updateWasteLog.rejected, (state, action) => {
        state.saveLoading = false
        state.error = (action.payload as any)?.message ?? action.error.message ?? 'Failed to update waste log'
      })

      // Locations
      // ResponseTransformInterceptor wraps all backend responses: { data: <actual body> }
      // Actual CrunchTime list body: { success, count, data: [...] }
      // So action.payload = { data: { success, count, data: [...] } }
      // and action.payload.data.data is the actual array.
      .addCase(fetchCrunchTimeLocations.pending, state => {
        state.locationsLoading = true
      })
      .addCase(fetchCrunchTimeLocations.fulfilled, (state, action) => {
        state.locationsLoading = false
        state.locations = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimeLocations.rejected, state => {
        state.locationsLoading = false
      })

      // Location by code (for period calculation)
      .addCase(fetchCrunchTimeLocationByCode.pending, state => {
        state.locationDetailsLoading = true
      })
      .addCase(fetchCrunchTimeLocationByCode.fulfilled, (state, action) => {
        state.locationDetailsLoading = false
        state.locationDetails = action.payload
      })
      .addCase(fetchCrunchTimeLocationByCode.rejected, state => {
        state.locationDetailsLoading = false
      })

      // Location by id — result is logged in the thunk; no state to update

      // Recipes
      .addCase(fetchCrunchTimeRecipes.pending, state => {
        state.recipesLoading = true
      })
      .addCase(fetchCrunchTimeRecipes.fulfilled, (state, action) => {
        state.recipesLoading = false
        state.recipes = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimeRecipes.rejected, state => {
        state.recipesLoading = false
      })

      // Package types
      .addCase(fetchCrunchTimePackageTypes.pending, state => {
        state.packageTypesLoading = true
      })
      .addCase(fetchCrunchTimePackageTypes.fulfilled, (state, action) => {
        state.packageTypesLoading = false
        state.packageTypes = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimePackageTypes.rejected, state => {
        state.packageTypesLoading = false
      })

      // Application users
      .addCase(fetchCrunchTimeApplicationUsers.pending, state => {
        state.applicationUsersLoading = true
      })
      .addCase(fetchCrunchTimeApplicationUsers.fulfilled, (state, action) => {
        state.applicationUsersLoading = false
        state.applicationUsers = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimeApplicationUsers.rejected, state => {
        state.applicationUsersLoading = false
      })

      // Company products
      .addCase(fetchCrunchTimeCompanyProducts.pending, state => {
        state.companyProductsLoading = true
      })
      .addCase(fetchCrunchTimeCompanyProducts.fulfilled, (state, action) => {
        state.companyProductsLoading = false
        state.companyProducts = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimeCompanyProducts.rejected, state => {
        state.companyProductsLoading = false
      })

      // Waste products (GET /crunchtime/waste-products)
      .addCase(fetchWasteProducts.pending, state => {
        state.wasteProductsLoading = true
      })
      .addCase(fetchWasteProducts.fulfilled, (state, action) => {
        state.wasteProductsLoading = false
        state.wasteProducts = Array.isArray(action.payload.data) ? action.payload.data : []
        state.wasteProductsHydratedForConcept =
          String(action.meta.arg.concept ?? '')
            .trim()
            .toUpperCase() || 'AUWASTE'
      })
      .addCase(fetchWasteProducts.rejected, (state, action) => {
        state.wasteProductsLoading = false
        state.wasteProductsHydratedForConcept =
          String(action.meta.arg.concept ?? '')
            .trim()
            .toUpperCase() || 'AUWASTE'
      })
  }
})

export const { resetWasteLoggingState, setPublished, setPublishReady } = gygEonWasteLoggingSlice.actions
export default gygEonWasteLoggingSlice.reducer
